import Api from './Api'
import LocalStorage from './LocalStorage'

/**
 * Service per gestione calendario locale
 * Eventi salvati solo sul database locale, senza integrazione Google
 */
class CalendarService {
  // ==================
  // EVENTI
  // ==================

  /**
   * Ottieni eventi dal calendario
   */
  async getEvents (timeMin, timeMax) {
    try {
      // Default: oggi -> +30 giorni
      if (!timeMin) {
        timeMin = new Date().toISOString()
      }
      if (!timeMax) {
        const futureDate = new Date()
        futureDate.setDate(futureDate.getDate() + 30)
        timeMax = futureDate.toISOString()
      }

      const response = await Api.get('/calendar/events', {
        timeMin,
        timeMax,
        includeClient: 'true' // Populate dati cliente
      })

      if (response.ok && response.data.events) {
        return response.data.events
      }

      return []
    } catch (error) {
      console.error('Error fetching events:', error)
      return []
    }
  }

  /**
   * Crea nuovo evento
   */
  async createEvent (eventData) {
    try {
      const response = await Api.post('/calendar/events', eventData)

      if (response.ok && response.data.event) {
        return response.data.event
      }

      throw new Error('Failed to create event')
    } catch (error) {
      console.error('Error creating event:', error)
      throw error
    }
  }

  /**
   * Modifica evento esistente
   */
  async updateEvent (eventId, eventData) {
    try {
      const response = await Api.patch(`/calendar/events/${eventId}`, eventData)

      if (response.ok && response.data.event) {
        return response.data.event
      }

      throw new Error('Failed to update event')
    } catch (error) {
      console.error('Error updating event:', error)
      throw error
    }
  }

  /**
   * Elimina evento
   */
  async deleteEvent (eventId) {
    try {
      const response = await Api.delete(`/calendar/events/${eventId}`)

      if (response.ok) {
        return { success: true }
      }

      throw new Error('Failed to delete event')
    } catch (error) {
      console.error('Error deleting event:', error)
      throw error
    }
  }

  /**
   * Toggle stato completamento evento
   */
  async toggleEventComplete (eventId) {
    try {
      const response = await Api.patch(`/calendar/events/${eventId}/toggle-complete`)

      if (response.ok && response.data.event) {
        return response.data.event
      }

      // Gestione errore 403 (non autorizzato a de-spuntare)
      if (response.status === 403) {
        throw new Error(response.data?.error || 'Non autorizzato')
      }

      throw new Error('Failed to toggle completion')
    } catch (error) {
      console.error('Error toggling event completion:', error)
      throw error
    }
  }

  // ==================
  // EVENTI CONDIVISI
  // ==================

  /**
   * Ottieni eventi condivisi azienda
   */
  async getSharedEvents (timeMin, timeMax) {
    try {
      const params = {}
      if (timeMin) params.timeMin = timeMin
      if (timeMax) params.timeMax = timeMax

      const response = await Api.get('/calendar/shared-events', params)

      if (response.ok && response.data.events) {
        return response.data.events
      }

      return []
    } catch (error) {
      console.error('Error fetching shared events:', error)
      return []
    }
  }

  /**
   * Condividi evento con utenti azienda
   */
  async shareEvent (eventId, sharedWith, isPrivate) {
    try {
      const response = await Api.post('/calendar/share-event', {
        eventId,
        sharedWith,
        isPrivate
      })

      if (response.ok) {
        return response.data.event
      }

      throw new Error('Failed to share event')
    } catch (error) {
      console.error('Error sharing event:', error)
      throw error
    }
  }

  /**
   * Ottieni lista utenti azienda per condivisione
   */
  async getCompanyUsers () {
    try {
      const response = await Api.get('/calendar/company-users')

      if (response.ok && response.data.users) {
        return response.data.users
      }

      return []
    } catch (error) {
      console.error('Error fetching company users:', error)
      return []
    }
  }

  /**
   * Ottieni lista clienti azienda
   */
  async getClients () {
    try {
      const response = await Api.get('/clienti', { azienda: LocalStorage.get('user').azienda._id })

      if (response.ok && response.data) {
        return response.data
      }

      return []
    } catch (error) {
      console.error('Error fetching clients:', error)
      return []
    }
  }

  // ==================
  // UTILITY
  // ==================

  /**
   * Conta eventi nei prossimi N giorni
   */
  countUpcomingEvents (events, days = 2) {
    if (!events || events.length === 0) return 0

    const now = new Date()
    const future = new Date()
    future.setDate(now.getDate() + days)

    return events.filter(event => {
      const start = new Date(event.startDate)
      return start >= now && start <= future
    }).length
  }

  /**
   * Filtra eventi per data - include eventi multi-giorno attivi in quella data
   */
  filterEventsByDate (events, date) {
    if (!events || events.length === 0) return []

    // Normalizza la data target a mezzanotte
    const targetDate = new Date(date)
    targetDate.setHours(0, 0, 0, 0)

    return events.filter(event => {
      const startDate = new Date(event.startDate)
      const endDate = new Date(event.endDate)

      // Normalizza le date degli eventi a mezzanotte
      const eventStart = new Date(startDate)
      eventStart.setHours(0, 0, 0, 0)

      const eventEnd = new Date(endDate)
      eventEnd.setHours(0, 0, 0, 0)

      // L'evento è visibile se la data target è tra inizio e fine (inclusivo)
      return targetDate >= eventStart && targetDate <= eventEnd
    })
  }
}

// Singleton instance
const calendarService = new CalendarService()
export default calendarService
